import { useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react';
import { Link, useParams } from 'react-router-dom';
import { resolveLink, verifyPin, packageUrl, fileStreamUrl, filePreviewUrl, getRecipientThread, postRecipientReply, getRecipientReferral, postRecipientReferralStatus } from '../api';
import { logEvent } from '../utils/analytics';
import { useStoreFreeGb, freeGbCopy } from '../hooks/useStoreFreeGb';
import { importTransferKey, unwrapKeyWithPin, decryptFramed } from '../utils/e2eCrypto';

// Read-only here. Same component and same jsonb the sender drew with, and the
// same way pages/LabPortal.jsx renders a paid lab case, so a framework looks
// identical whichever rail it arrived on. Lazy: only a partial-denture Rx
// loads the tooth geometry.
const RpdDesigner = lazy(() => import('@intake-dental/shared-components/components/RpdDesigner'));

const PREVIEWABLE = /\.(dcm|dicom|pdf|jpe?g|png|gif|webp|bmp|stl|ply|obj)$/i;
const MESH = /\.(stl|ply|obj)$/i;

export default function RecipientPage() {
  const { token } = useParams();
  const [state, setState] = useState({ phase: 'loading' });

  useEffect(() => {
    (async () => {
      try {
        const info = await resolveLink(token);
        setState({ phase: 'pin', info });
      } catch (e) {
        setState({ phase: 'error', err: e.message });
      }
    })();
  }, [token]);

  return (
    // Once unlocked, the whole section goes wide so the file list, notices and
    // the radiograph viewer line up at full window width on desktop. The PIN
    // and loading screens stay a narrow centred column.
    <div className={state.phase === 'ready' ? 'recipient-wide' : 'narrow'} style={{ margin: '40px auto' }}>
      {state.phase === 'loading' && (
        <div className="card"><p className="muted">Loading secure package…</p></div>
      )}
      {state.phase === 'error' && (
        <div className="card">
          <h1>Can't open this link</h1>
          <p className="lead">{state.err}</p>
          <p className="muted">Secure links expire automatically. If this file package was meant for you, ask the sender to create a new transfer — it only takes a minute.</p>
        </div>
      )}
      {state.phase === 'pin'  && <PinEntry token={token} info={state.info} onUnlocked={(unlock) => setState({ phase: 'ready', token, unlock })} />}
      {state.phase === 'ready'&& <ReadyToDownload token={state.token} unlock={state.unlock} />}
    </div>
  );
}

function PinEntry({ token, info, onUnlocked }) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [err, setErr]       = useState(null);
  const [loading, setLoading] = useState(false);

  const setDigit = (i, v) => {
    v = v.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 3) document.getElementById(`pin-${i + 1}`)?.focus();
  };

  const onSubmit = async () => {
    const pin = digits.join('');
    if (pin.length !== 4) return;
    setErr(null); setLoading(true);
    try {
      const r = await verifyPin(token, pin);
      onUnlocked({ ...r, pin });
    } catch (e) {
      setErr(e.data?.error || e.message);
      if (e.data?.attempts_remaining !== undefined) {
        setErr(`${e.message} (${e.data.attempts_remaining} attempts left)`);
      }
    }
    setLoading(false);
  };

  return (
    <div className="card">
      <h1>Secure package</h1>
      <p className="lead">
        <strong>{info.sender_email}</strong> has shared a secure file package with you.
      </p>
      <p className="muted">
        {info.file_count} file{info.file_count === 1 ? '' : 's'}
        {' · '}{formatBytes(info.total_bytes || 0)}
        {' · '}expires {new Date(info.expires_at).toLocaleDateString()}
      </p>

      <hr style={{ border: 0, borderTop: '1px solid #e5e7eb', margin: '20px 0' }} />

      <h2>Enter your 4-digit PIN</h2>
      <p className="muted">The sender will have shared this with you by phone or text.</p>

      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      <div className="pin-grid">
        {digits.map((d, i) => (
          <input
            key={i}
            id={`pin-${i}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digits[i] && i > 0) {
                document.getElementById(`pin-${i - 1}`)?.focus();
              }
            }}
            autoFocus={i === 0}
          />
        ))}
      </div>

      {err && <div className="error" style={{ textAlign: 'center' }}>{err}</div>}

      <button
        type="submit"
        className="btn btn-primary btn-block btn-large"
        style={{ marginTop: 16 }}
        disabled={digits.join('').length !== 4 || loading}
      >
        {loading ? 'Verifying…' : 'Unlock package'}
      </button>
      </form>
    </div>
  );
}

// `access` swaps the link-token URLs for another credential's: the Send
// dashboard opens a package its signed-in addressee received with a download
// JWT alone (pages/ReceivedPackage.jsx), so there is no /r/<token> to build
// from. In that owner mode the recipient-only extras (reply thread, referral
// card, the claim/return CTAs that need the link token) give way to `ctas`.
export function ReadyToDownload({ token, unlock, access: accessProp = null, ctas = null }) {
  // lab_rx is null for every non-Rx package, and absent entirely in owner mode
  // (ReceivedPackage builds `unlock` from the download-JWT path), so the card
  // below simply does not render rather than needing a mode check.
  const { transfer, files, pin, download_token, lab_rx: labRx } = unlock;
  const ownerMode = !!accessProp;
  const access = accessProp || {
    stream: (fileId, opts) => fileStreamUrl(token, fileId, download_token, opts),
    preview: (fileId) => filePreviewUrl(token, fileId, download_token),
    zip: packageUrl(token, { download_token, pin }),
  };
  // Free Store allowance from the API (never hardcoded; see the hook).
  const storeFreeGb = useStoreFreeGb();
  const downloadHref = access.zip;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sampled, setSampled] = useState(null); // { shown, total } when a phone gets a subset
  const [loadingId, setLoadingId] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const iframeRef = useRef(null);
  const viewerReady = useRef(null); // promise resolved on intake-viewer-ready
  const sentIds = useRef(new Set());
  const isE2E = transfer.e2e === true;
  const tkRef = useRef(null); // CryptoKey for e2e transfers

  // Zero-knowledge key: prefer the URL fragment (#k= — never sent to any
  // server); fall back to unwrapping the stored blob with the PIN.
  async function getTransferKey() {
    if (tkRef.current) return tkRef.current;
    const m = window.location.hash.match(/[#&]k=([A-Za-z0-9_-]{40,})/);
    if (m) {
      tkRef.current = await importTransferKey(m[1]);
    } else if (transfer.e2e_key_wrapped) {
      const { key } = await unwrapKeyWithPin(transfer.e2e_key_wrapped, pin);
      tkRef.current = key;
    } else {
      throw new Error('Missing decryption key — open the exact link you were sent');
    }
    return tkRef.current;
  }

  // One byte range of a file's ciphertext, [from, to). Resolves null when the
  // server answered with the whole file instead (a range it can't serve).
  async function fetchRange(f, from, to, onBytes) {
    const url = `${access.stream(f.id)}&range=${from}-${to - 1}`;
    const res = await fetch(url);
    if (res.status === 401) throw new Error('Session expired — re-enter your PIN');
    if (res.status !== 206 || !res.body?.getReader) {
      try { await res.body?.cancel(); } catch { /* ignore */ }
      return null;
    }
    const total = Number((res.headers.get('content-range') || '').split('/')[1]) || f.size_bytes;
    const out = new Uint8Array(to - from);
    const reader = res.body.getReader();
    let got = 0;
    let lastReport = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      out.set(value.subarray(0, Math.max(0, out.length - got)), got);
      got = Math.min(out.length, got + value.length);
      const now = performance.now();
      if (onBytes && now - lastReport > 250) { lastReport = now; onBytes(got); }
    }
    onBytes?.(got);
    return { bytes: out.subarray(0, got), total };
  }

  async function fetchDecrypted(f, onProgress, onPreview, onStream) {
    // An end-to-end file is kept in this browser's cache exactly as it was
    // downloaded — still encrypted, the key is only in the link — so opening
    // the link again, or reloading mid-look, doesn't re-download a 200 MB scan.
    if (isE2E) {
      const cached = await readCachedCipher(f.id);
      if (cached) {
        onProgress?.(cached.byteLength, cached.byteLength);
        const blob = await decryptFramed(cached, await getTransferKey());
        return new File([blob], f.original_filename, { type: f.content_type || 'application/octet-stream' });
      }
    }
    // A whole-volume DICOM (a CBCT): stream it into the viewer from the middle
    // outward, a run of slices at a time — see utils/progressiveDicom.js.
    if (isE2E && onPreview && onStream && isDicomName(f.original_filename) && f.size_bytes >= SERIES_SLICE_MAX_BYTES) {
      let started = false;
      try {
        const { streamVolume } = await import('../utils/progressiveDicom.js');
        const key = await getTransferKey();
        // The sender's browser may have left a 16-slice preview (~1–3 MB):
        // something to scroll within a second or two, sharpened region by
        // region as the real slices stream in.
        try {
          const pr = await fetch(access.preview(f.id));
          if (pr.ok) {
            const blob = await decryptFramed(new Uint8Array(await pr.arrayBuffer()), key);
            onPreview(new File([blob], f.original_filename, { type: 'application/dicom' }));
          }
        } catch (e) {
          console.warn('quick preview unavailable', e);
        }
        const r = await streamVolume({
          fetchRange: (from, to, onBytes) => fetchRange(f, from, to, onBytes),
          key,
          name: f.original_filename,
          onStart: (frames) => { started = true; onStream({ kind: 'start', frames }); },
          onFrames: (start, count, file) => onStream({ kind: 'frames', start, count, file }),
          onProgress: onProgress || (() => {}),
        });
        if (r?.cipher) {
          writeCachedCipher(f.id, r.cipher.buffer, transfer.expires_at);
          if (r.streamed) {
            onStream({ kind: 'done' });
            // The viewer already holds every slice; this File is for callers
            // that want the bytes, and is not posted to the viewer again.
            const file = new File(r.plain, f.original_filename, { type: f.content_type || 'application/octet-stream' });
            file.streamedToViewer = true;
            return file;
          }
          if (started) { onStream({ kind: 'abort' }); started = false; }
          const blob = await decryptFramed(r.cipher, key);
          return new File([blob], f.original_filename, { type: f.content_type || 'application/octet-stream' });
        }
      } catch (e) {
        if (/re-enter your PIN/.test(e.message)) throw e;
        console.warn('streamed load failed, downloading whole file', e);
      }
      if (started) onStream({ kind: 'abort' });
    }
    const res = await fetch(access.stream(f.id));
    if (!res.ok) throw new Error(res.status === 401 ? 'Session expired — re-enter your PIN' : `Couldn't load file (${res.status})`);
    // Stream so a large scan (a CBCT can be 200 MB) can report progress —
    // but cheaply. Chunks go straight into one pre-sized buffer (no second
    // 200 MB copy at the end) and progress is reported at most 4x a second.
    // Posting to the viewer on EVERY chunk ran thousands of messages on the
    // thread reading the download and made a CBCT load ~2x slower.
    let buf;
    const total = Number(res.headers.get('content-length')) || 0;
    if (onProgress && res.body && res.body.getReader && total > 0) {
      const reader = res.body.getReader();
      let out = new Uint8Array(total);
      let loaded = 0;
      let lastReport = 0;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (loaded + value.length > out.length) { // server sent more than it said
          const grown = new Uint8Array(Math.max(out.length * 2, loaded + value.length));
          grown.set(out.subarray(0, loaded));
          out = grown;
        }
        out.set(value, loaded);
        loaded += value.length;
        const now = performance.now();
        if (now - lastReport > 250) { lastReport = now; onProgress(loaded, total); }
      }
      onProgress(loaded, total);
      buf = loaded === out.length ? out.buffer : out.slice(0, loaded).buffer;
    } else {
      buf = await res.arrayBuffer();
    }
    if (!isE2E) return new File([buf], f.original_filename, { type: f.content_type || 'application/octet-stream' });
    writeCachedCipher(f.id, buf, transfer.expires_at);
    const key = await getTransferKey();
    const blob = await decryptFramed(buf, key);
    return new File([blob], f.original_filename, { type: f.content_type || 'application/octet-stream' });
  }

  const [savingId, setSavingId] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [replied, setReplied] = useState(false);
  const [officeName, setOfficeName] = useState(null);
  async function saveFile(f) {
    setPreviewErr(null);
    setSavingId(f.id);
    try {
      const file = await fetchDecrypted(f);
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url; a.download = f.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      setPreviewErr(e.message);
    } finally {
      setSavingId(null);
    }
  }

  // Single-slice DICOMs are small; a file this size or larger is a whole
  // volume (multi-frame CBCT) and is previewed on its own.
  const SERIES_SLICE_MAX_BYTES = 20 * 1024 * 1024;
  const SCAN_SERIES_MIN_FILES = 40;
  // A phone can't hold a 640-slice scan (~650 MB) in the browser: it gets an
  // evenly spaced subset — the whole scan at a coarser slice spacing.
  const PHONE_MAX_SLICES = 160;
  const isPhone = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches && Math.min(window.innerWidth, window.innerHeight) < 900;
  const isDicomName = (n) => /\.(dcm|dicom)$/i.test(n || '');

  const postToViewer = (msg) => iframeRef.current?.contentWindow?.postMessage(msg, window.location.origin);

  // The iframe loads the same self-contained viewer that ships in the zip;
  // files are fetched here (dt-token auth) and posted in as File objects.
  // Clicking Preview on one slice of a slice-per-file scan sends the whole
  // series so it opens as one scrollable stack, not a single slice.
  function openViewer() {
    if (!previewOpen) {
      setPreviewOpen(true);
      viewerReady.current = new Promise((resolve) => {
        const onMsg = (e) => {
          if (e.origin === window.location.origin && e.data?.type === 'intake-viewer-ready') {
            window.removeEventListener('message', onMsg);
            resolve();
          }
        };
        window.addEventListener('message', onMsg);
      });
    }
  }

  // Fetch + decrypt the files the viewer does not have yet, reporting combined
  // progress, then hand them over. `grid` opens the all-images grid.
  async function sendToViewer(list, label, { grid = false, focus = null } = {}) {
    const toSend = list.filter((x) => !sentIds.current.has(x.id));
    const out = [];
    let previewShown = false;
    if (toSend.length) {
      // Downloads start now, not once the viewer page has loaded: on a large
      // scan that is a second or two of the wait for free. Progress posted
      // before the viewer listens is simply dropped; the next report lands.
      // Up to 4 downloads at once (an FMX is ~18 files), one combined
      // progress figure. Results keep the transfer's order.
      const totalBytes = toSend.reduce((n, x) => n + (x.size_bytes || 0), 0);
      const loadedBy = new Map();
      const startedAt = performance.now();
      const report = () => postToViewer({
        type: 'intake-viewer-loading', name: label,
        loaded: [...loadedBy.values()].reduce((n, v) => n + v, 0), total: totalBytes,
        elapsedMs: performance.now() - startedAt,
      });
      const results = new Array(toSend.length);
      let next = 0;
      // One whole volume: its quick preview, then its slices as they stream in.
      const onPreview = toSend.length === 1
        ? async (file) => {
          await viewerReady.current;
          previewShown = true;
          postToViewer({ type: 'intake-viewer-files', files: [file], partial: true });
        }
        : undefined;
      const onStream = toSend.length === 1
        ? async (ev) => {
          await viewerReady.current;
          postToViewer({ type: 'intake-viewer-stream', name: toSend[0].original_filename, ...ev });
        }
        : undefined;
      const worker = async () => {
        while (next < toSend.length) {
          const i = next++;
          const x = toSend[i];
          results[i] = await fetchDecrypted(x, (loaded) => { loadedBy.set(x.id, loaded); report(); }, onPreview, onStream);
          loadedBy.set(x.id, x.size_bytes || 0);
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, toSend.length) }, worker));
      const streamed = results.some((r) => r?.streamedToViewer);
      out.push(...results.filter((r) => !r?.streamedToViewer));
      toSend.forEach((x) => sentIds.current.add(x.id));
      if (streamed && !out.length && !grid) return; // the viewer already has it
    }
    await viewerReady.current;
    // An empty batch with grid:true still switches an open viewer to the grid.
    if (out.length || grid || focus) {
      postToViewer({ type: 'intake-viewer-files', files: out, grid, focus, replacePartial: previewShown });
    }
  }

  // Every image in the transfer on one screen — an FMX's PAs and bitewings —
  // each expandable on click. Whole-volume DICOMs (CBCTs) are left out; they
  // open on their own with Preview.
  // A slice-per-file CBCT (dozens to hundreds of small .dcm files) is ONE
  // scan: it opens with "Open the scan", not as hundreds of grid thumbnails.
  const sliceFiles = files.filter((x) => isDicomName(x.original_filename) && x.size_bytes < SERIES_SLICE_MAX_BYTES);
  const isScanSeries = sliceFiles.length >= SCAN_SERIES_MIN_FILES;
  const gridFiles = files.filter((x) => PREVIEWABLE.test(x.original_filename) &&
    !/\.pdf$/i.test(x.original_filename) &&
    !(isDicomName(x.original_filename) && x.size_bytes >= SERIES_SLICE_MAX_BYTES) &&
    !(isScanSeries && isDicomName(x.original_filename)));
  const [allFilesShown, setAllFilesShown] = useState(false);
  async function viewAll() {
    setPreviewErr(null);
    setLoadingId('all');
    try {
      openViewer();
      await sendToViewer(gridFiles, `${gridFiles.length} images`, { grid: true });
      logEvent('recipient_preview_all', { count: gridFiles.length });
      iframeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (e) {
      setPreviewErr(e.message);
      postToViewer({ type: 'intake-viewer-error', message: `Couldn't load the images: ${e.message}` });
    } finally {
      setLoadingId(null);
    }
  }

  async function previewFile(f) {
    setPreviewErr(null);
    setLoadingId(f.id);
    try {
      openViewer();
      let series = isDicomName(f.original_filename) && f.size_bytes < SERIES_SLICE_MAX_BYTES
        ? files.filter((x) => isDicomName(x.original_filename) && x.size_bytes < SERIES_SLICE_MAX_BYTES)
        : [f];
      if (isPhone && series.length > PHONE_MAX_SLICES) {
        // Evenly spaced across the whole scan, always keeping the one tapped.
        const step = series.length / PHONE_MAX_SLICES;
        const picked = new Set(Array.from({ length: PHONE_MAX_SLICES }, (_, i) => series[Math.floor(i * step)]));
        picked.add(f);
        series = series.filter((x) => picked.has(x));
        setSampled({ shown: series.length, total: files.filter((x) => isDicomName(x.original_filename) && x.size_bytes < SERIES_SLICE_MAX_BYTES).length });
      }
      // `focus` makes the viewer open the file that was clicked (or the series
      // it belongs to), not whichever arrived last.
      await sendToViewer(series, series.length > 1 ? `${series.length} files` : f.original_filename, { focus: f.original_filename });
      logEvent('recipient_preview', { ext: (f.original_filename.split('.').pop() || '').toLowerCase() });
      iframeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (e) {
      setPreviewErr(e.message);
      postToViewer({ type: 'intake-viewer-error', message: `Couldn't load the preview: ${e.message}` });
    } finally {
      setLoadingId(null);
    }
  }

  // Open the viewer as soon as the files are unlocked — the recipient came to
  // look at the x-ray, so don't make them find a button. Several images open
  // in the grid; one opens directly. Above AUTO_PREVIEW_MAX_BYTES it waits for
  // a click so opening a link on phone data doesn't start a huge download.
  const AUTO_PREVIEW_MAX_BYTES = 300 * 1024 * 1024;
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current) return; // React strict mode mounts effects twice
    autoStarted.current = true;
    const bytes = (list) => list.reduce((n, x) => n + (x.size_bytes || 0), 0);
    // A slice-per-file scan opens itself only when it's small enough not to
    // surprise anyone on phone data; otherwise "Open the scan" waits.
    if (isScanSeries) {
      if (bytes(sliceFiles) <= AUTO_PREVIEW_MAX_BYTES) previewFile(sliceFiles[Math.floor(sliceFiles.length / 2)]);
      return;
    }
    if (gridFiles.length > 1 && bytes(gridFiles) <= AUTO_PREVIEW_MAX_BYTES) {
      viewAll();
      return;
    }
    const previewable = files.filter((x) => PREVIEWABLE.test(x.original_filename));
    // Prefer an image or scan over a PDF.
    const first = previewable.find((x) => !/\.pdf$/i.test(x.original_filename)) || previewable[0];
    if (first && first.size_bytes <= AUTO_PREVIEW_MAX_BYTES) previewFile(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card">
      <h1>{files.length > 0 && files.every((x) => MESH.test(x.original_filename || ''))
        ? (files.length === 1 ? 'Your 3D scan is ready' : 'Your 3D scans are ready')
        : 'Your files are ready'}</h1>
      {transfer.title && <p className="lead">{transfer.title}</p>}
      {transfer.message && (
        <div style={{ background: '#f1f5f9', padding: 14, borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          <div className="muted" style={{ marginBottom: 4 }}>Message from sender:</div>
          {transfer.message}
        </div>
      )}
      {labRx && <LabRxCard rx={labRx} />}
      <p className="muted">
        {files.length} file{files.length === 1 ? '' : 's'} · {formatBytes(transfer.total_bytes || 0)} · expires {new Date(transfer.expires_at).toLocaleDateString()}
      </p>
      {isE2E && (
        <button type="button" className="how-link" onClick={() => setHowOpen(true)}>
          🔒 End-to-end encrypted{transfer.details_sealed ? ' · Extra Security sealed' : ''} · <span>How it works</span>
        </button>
      )}
      {howOpen && <HowItWorksModal sealed={transfer.details_sealed === true} onClose={() => setHowOpen(false)} />}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
        {isScanSeries && (
          <button
            className="btn btn-primary"
            disabled={!!loadingId}
            onClick={() => previewFile(sliceFiles[Math.floor(sliceFiles.length / 2)])}
          >
            {loadingId ? 'Loading the scan…' : `▶ Open the scan (${sliceFiles.length} slices)`}
          </button>
        )}
        {gridFiles.length > 1 && (
          <button
            className={isScanSeries ? 'btn' : 'btn btn-primary'}
            disabled={loadingId === 'all'}
            onClick={viewAll}
          >
            {loadingId === 'all' ? 'Loading images…' : `▦ View all ${gridFiles.length} images`}
          </button>
        )}
        {!isE2E && files.length > 1 && (
          <a className="btn" href={downloadHref} download>⬇ Download all ({files.length} files, .zip)</a>
        )}
      </div>
      {sampled && (
        <p className="muted" style={{ marginTop: 8, fontSize: 13 }}>
          On a phone the scan opens with {sampled.shown} of its {sampled.total} slices, evenly spaced. Open it on a computer, or download it, for every slice.
        </p>
      )}

      <ul className="files-list" style={{ marginTop: 16 }}>
        {(allFilesShown || files.length <= 12 ? files : files.slice(0, 8)).map((f) => (
          <li key={f.id}>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_filename}</span>
            <span className="muted" style={{ marginRight: 10 }}>{formatBytes(f.size_bytes)}</span>
            {PREVIEWABLE.test(f.original_filename) && (
              <button
                className="btn"
                style={{ padding: '4px 10px', fontSize: 13, marginRight: 6 }}
                disabled={loadingId === f.id}
                onClick={() => previewFile(f)}
              >
                {loadingId === f.id ? 'Loading…' : 'Preview'}
              </button>
            )}
            {isE2E ? (
              <button
                className="btn"
                style={{ padding: '4px 10px', fontSize: 13 }}
                disabled={savingId === f.id}
                onClick={() => saveFile(f)}
              >
                {savingId === f.id ? 'Downloading…' : '⬇ Download'}
              </button>
            ) : (
              <a
                className="btn"
                style={{ padding: '4px 10px', fontSize: 13 }}
                href={access.stream(f.id, { download: true })}
                download={f.original_filename}
              >
                ⬇ Download
              </a>
            )}
          </li>
        ))}
      </ul>
      {!allFilesShown && files.length > 12 && (
        <button type="button" className="how-link" onClick={() => setAllFilesShown(true)}>Show all {files.length} files</button>
      )}

      {previewErr && <div className="error" style={{ marginTop: 10 }}>{previewErr}</div>}

      {previewOpen && (
        <iframe
          ref={iframeRef}
          src="/viewer.html"
          title="Secure file viewer"
          className="preview-frame"
        />
      )}

      {!ownerMode && <ReferralCard token={token} downloadToken={download_token} />}
      {!ownerMode && <ReplyPanel token={token} downloadToken={download_token} onReplied={() => setReplied(true)} onOffice={setOfficeName} />}

      {!isE2E && (
        <a className="btn btn-primary btn-block btn-large" style={{ marginTop: 16 }} href={downloadHref} download>
          ⬇ Download secure package (.zip)
        </a>
      )}
      {!isE2E && (
        <p className="muted" style={{ marginTop: 14, fontSize: 13 }}>
          The package includes a <strong>viewer.html</strong> — double-click it after unzipping,
          then drag the <code>files</code> folder in to view DICOM x-rays, images, and PDFs
          right in your browser. Nothing is uploaded back to us.
        </p>
      )}

      {ownerMode ? ctas : (
      <div className="recipient-ctas">
      {/* An Intake office: file this conversation in its dashboard inbox,
          whatever email it was sent to. The unlock token rides the URL
          fragment, which never reaches a server log. */}
      {download_token && (
        <div className="recipient-cta" style={{ background: '#f8fafc', border: '1px solid #cbd5e1' }}>
          <p style={{ margin: 0, fontSize: 14, color: '#0f172a' }}>
            <strong>Use Intake at your office?</strong>{' '}
            Sign in and this package, and anything else {officeName || 'this office'} sends to this
            email, shows up in your dashboard inbox.
          </p>
          <a
            className="btn btn-primary"
            href={`https://app.intake.dental/claim-send#t=${encodeURIComponent(token)}&dt=${encodeURIComponent(download_token)}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logEvent('save_to_dashboard_click', { source: 'recipient_page' })}
          >
            Save to my Intake dashboard →
          </a>
        </div>
      )}
      {/* Store: keep the package permanently under your own key — for any
          recipient, practice or not. The unlock token rides the fragment. */}
      {download_token && !transfer?.e2e && (
        <div className="recipient-cta" style={{ background: '#f0fdfa', border: '1px solid #99f6e4' }}>
          <p style={{ margin: 0, fontSize: 14, color: '#134e4a' }}>
            <strong>Keep these files.</strong>{' '}
            This package expires. Save it to your Store — encrypted under your own key, filed under {officeName || 'the sending office'}, yours to share again. {freeGbCopy(storeFreeGb, { suffix: 'free' }).replace(/^free to start$/, 'Free to start')}.
          </p>
          <a
            className="btn btn-primary"
            href={`https://store.intake.dental/claim#t=${encodeURIComponent(token)}&dt=${encodeURIComponent(download_token)}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logEvent('save_to_store_click', { source: 'recipient_page' })}
          >
            Save to my Store →
          </a>
        </div>
      )}
      <div className="recipient-cta" style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
        <p style={{ margin: 0, fontSize: 14, color: '#0c4a6e' }}>
          <strong>Send files back to {officeName || transfer.sender_email || 'the office'}.</strong>{' '}
          Reports, post-op scans or records go straight back, filed in this same case.
          Free, HIPAA-compliant, no account setup.
        </p>
        <Link
          className="btn btn-primary"
          to={`/?return=${encodeURIComponent(token)}&ref=recipient-page`}
          onClick={() => logEvent('send_back_cta_click', { source: 'recipient_page' })}
        >
          Send files back →
        </Link>
      </div>

      {/* The relationship CTA: this specialist just watched the product work,
          referred by an office they already trust — the warmest moment to
          turn a one-shot transfer into a standing Bridge connection. */}
      <div className="recipient-cta" style={{ background: '#ecfeff', border: '1px solid #a5f3fc' }}>
        <p style={{ margin: 0, fontSize: 14, color: '#155e75' }}>
          {replied ? (
            <>
              <strong>Continue this conversation on Bridge.</strong>{' '}
              Your reply is in the case thread. On Bridge you get every case with this office in one
              place, X-rays attached, for your whole team. Free, two-minute setup.
            </>
          ) : (
            <>
              <strong>Work with {transfer.sender_email ? 'this office' : 'referring offices'} often?</strong>{' '}
              Bridge is free, secure chat between dental offices — talk cases through with the
              X-rays attached, instead of trading faxes and voicemails. Free for your whole team,
              two-minute setup.
            </>
          )}
        </p>
        <a
          className="btn btn-primary"
          href="https://intake.dental/bridge?src=send_recipient"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logEvent('bridge_cta_click', { source: 'recipient_page' })}
        >
          Connect on Bridge — it's free →
        </a>
        {/* De-identified only: the forum is public. The case itself stays
            behind the PIN; what goes to the forum is the question. */}
        <p style={{ margin: '10px 0 0', fontSize: 13 }}>
          <a
            href="https://intake.dental/forum?src=send_recipient"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logEvent('forum_cta_click', { source: 'recipient_page' })}
          >
            Discuss a case like this (de-identified) on the dentist forum →
          </a>
        </p>
      </div>
      </div>
      )}

      <p className="muted" style={{ marginTop: 12, fontSize: 13, textAlign: 'center' }}>
        Dental office?{' '}
        <a
          href="https://intake.dental/dentists/claim?src=secure_transfer"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logEvent('claim_cta_click', { source: 'recipient_page' })}
        >
          Claim your free Intake Dental listing
        </a>
      </p>
    </div>
  );
}

function formatBytes(b) {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// Reply to the sending office from under the preview: the recent messages of
// the case thread this package was filed into, and a box to answer in. The
// office is emailed by the thread's own notification; the panel only appears
// when the package has a thread.
/**
 * The prescription, for a lab that opened the package.
 *
 * Shown above the files because it is the instruction and they are the
 * evidence. Everything here comes from the server's describe(), so the labels
 * are the ones SendLabRxService owns rather than a second copy that can drift.
 *
 * No status rail, unlike ReferralCard. A specialist confirms and schedules
 * against a referral row that exists; an account-free Rx has no such row yet,
 * and a button that silently did nothing would be worse than no button.
 */
function LabRxCard({ rx }) {
  const due = rx.dueDate
    // Parsed as UTC noon: a bare YYYY-MM-DD parses as UTC midnight, which
    // renders as the day BEFORE for every reader west of Greenwich.
    ? new Date(`${rx.dueDate}T12:00:00Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  const line = [rx.materialLabel, rx.shade ? `shade ${rx.shade}` : null].filter(Boolean).join(' · ');

  return (
    <section
      aria-label="Lab prescription"
      style={{ border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 10, padding: 14, marginBottom: 16 }}
    >
      <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
        Lab prescription
      </div>
      <strong style={{ fontSize: 15 }}>{rx.caseTypeLabel}</strong>
      {line && <div style={{ fontSize: 14, marginTop: 2 }}>{line}</div>}
      {due && <div style={{ fontSize: 14, marginTop: 2 }}>Needed by {due}</div>}
      {rx.frameworkSummary && (
        <div style={{ fontSize: 14, marginTop: 6 }}>
          <strong>Framework:</strong> {rx.frameworkSummary}
          {rx.kennedyLabel ? ` (${rx.kennedyLabel})` : ''}
        </div>
      )}
      {rx.hasDesign && rx.design && (
        <div style={{ marginTop: 10, background: '#fff', borderRadius: 8, padding: 8 }}>
          <Suspense fallback={<p className="muted" style={{ fontSize: 13, margin: 0 }}>Loading the framework…</p>}>
            <RpdDesigner value={rx.design} readOnly />
          </Suspense>
        </div>
      )}
    </section>
  );
}

// A referral from the sending office: the specialist confirms and schedules
// right here, and the office's referral timeline updates. Only shown when the
// package was sent with a dashboard referral.
function ReferralCard({ token, downloadToken }) {
  const [info, setInfo] = useState(null);
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getRecipientReferral(token, downloadToken).then(setInfo).catch(() => setInfo(null));
  }, [token, downloadToken]);

  if (!info?.available) return null;
  const r = info.referral || {};
  const status = info.status;

  const mark = async (next) => {
    setBusy(next); setErr(null);
    try {
      await postRecipientReferralStatus(token, downloadToken, next, next === 'scheduled' ? date : undefined);
      setInfo((i) => ({ ...i, status: next, appointment_date: next === 'scheduled' ? date || i.appointment_date : i.appointment_date }));
      logEvent('referral_status_click', { status: next, source: 'recipient_page' });
    } catch (e) {
      setErr(e.data?.error || e.message || 'That did not save');
    } finally {
      setBusy(null);
    }
  };

  const fmt = (d) => new Date(`${String(d).slice(0, 10)}T12:00:00Z`).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <section className={`referral-card${r.isUrgent ? ' urgent' : ''}`} aria-label="Referral">
      <div className="referral-head">
        <strong>Referral from {info.office_name}</strong>
        <span>{[r.specialtyLabel, r.urgencyLabel].filter(Boolean).join(' · ')}</span>
      </div>
      {status === 'scheduled' || status === 'completed' ? (
        <p className="referral-done">
          ✓ {status === 'completed' ? 'Marked completed' : `Scheduled${info.appointment_date ? ` for ${fmt(info.appointment_date)}` : ''}`}. {info.office_name} can see this.
        </p>
      ) : (
        <div className="referral-actions">
          {status === 'received' ? (
            <span className="referral-done">✓ Received</span>
          ) : (
            <button type="button" className="btn btn-secondary" disabled={!!busy} onClick={() => mark('received')}>
              {busy === 'received' ? 'Saving…' : 'Confirm received'}
            </button>
          )}
          <span className="referral-schedule">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Appointment date" />
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => mark('scheduled')}>
              {busy === 'scheduled' ? 'Saving…' : 'Mark scheduled'}
            </button>
          </span>
        </div>
      )}
      {err && <div className="error" style={{ marginTop: 6 }}>{err}</div>}
      <p className="muted" style={{ margin: '8px 0 0', fontSize: 13 }}>
        Finished? <Link to={`/?return=${encodeURIComponent(token)}&ref=referral-card`}>Send your report back</Link>, filed with this referral.
      </p>
      <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
        Want referrals like this from every office?{' '}
        <a
          href="https://intake.dental/bridge?src=send_referral_card&intent=referral_link"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logEvent('referral_link_cta_click', { source: 'referral_card' })}
        >
          Get your free referral link
        </a>
        : GPs send the patient, the reason and the imaging straight to you.
      </p>
    </section>
  );
}

function ReplyPanel({ token, downloadToken, onReplied, onOffice }) {
  const [state, setState] = useState({ available: false, office: '', messages: [] });
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const listRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await getRecipientThread(token, downloadToken);
      setState({ available: r.available === true, office: r.office_name || 'the office', messages: r.messages || [] });
      if (r.office_name) onOffice?.(r.office_name);
    } catch { /* keep what we have; the panel is a convenience */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, downloadToken]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [state.messages.length]);

  if (!state.available) return null;

  const send = async () => {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await postRecipientReply(token, downloadToken, body);
      setState((s) => ({ ...s, messages: [...s.messages, r.message] }));
      setDraft('');
      onReplied?.();
    } catch (e) {
      setErr(e.data?.error || e.message || 'Your reply did not send');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="reply-panel" aria-label={`Reply to ${state.office}`}>
      <h2>Reply to {state.office}</h2>
      {state.messages.length > 0 && (
        <ol className="reply-list" ref={listRef}>
          {state.messages.map((m) => (
            <li key={m.id || m.created_at} className={m.mine ? 'mine' : m.kind && m.kind !== 'text' ? 'system' : 'theirs'}>
              <span className="reply-meta">{m.author_label} · {new Date(m.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              <span className="reply-body">{m.body}</span>
            </li>
          ))}
        </ol>
      )}
      <textarea
        rows={3}
        value={draft}
        maxLength={2000}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
        placeholder="Got it, thanks. We will call the patient to schedule."
      />
      {err && <div className="error" style={{ marginTop: 6 }}>{err}</div>}
      <div className="reply-actions">
        <span className="muted">
          Encrypted and sent to {state.office}. They are emailed when you reply.{' '}
          <Link to={`/?return=${encodeURIComponent(token)}&ref=reply-panel`} onClick={() => logEvent('send_back_cta_click', { source: 'reply_panel' })}>Attach files</Link>
        </span>
        <button type="button" className="btn btn-primary" disabled={!draft.trim() || busy} onClick={send}>
          {busy ? 'Sending…' : 'Send reply'}
        </button>
      </div>
    </section>
  );
}

const HOW_STEPS = [
  { icon: '🔐', title: "Locked on the sender's computer", body: 'Files are encrypted in their browser before anything is uploaded.' },
  { icon: '☁️', title: 'We only hold scrambled data', body: 'Our servers store the locked files and cannot open them.' },
  { icon: '🔑', title: 'Your link and PIN are the key', body: 'The key travels in your link and never reaches our servers.' },
  { icon: '💻', title: 'Unlocked on your device', body: 'Preview and Download open the files right here. Nothing to install.' },
];

function HowItWorksModal({ sealed = false, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="how-overlay" onClick={onClose} role="presentation">
      <div className="how-modal" role="dialog" aria-modal="true" aria-labelledby="how-title" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="how-close" onClick={onClose} aria-label="Close">✕</button>
        <h2 id="how-title">How your files stay private</h2>
        <ol className="how-steps">
          {HOW_STEPS.map((st, i) => (
            <li key={st.title}>
              <span className="how-icon" aria-hidden="true">{st.icon}</span>
              <span className="how-num">{i + 1}</span>
              <strong>{st.title}</strong>
              <span className="how-body">{st.body}</span>
            </li>
          ))}
        </ol>
        {sealed && (
          <div className="how-sealed">
            <span className="how-sealed-icon" aria-hidden="true">🛡️</span>
            <div>
              <strong>Plus Extra Security on the details</strong>
              <span>
                This package's title, message and file names are sealed a second time on our side with
                glyph encryption layered over AES-256-GCM, so even the labels around your files are never
                stored readable.
              </span>
            </div>
          </div>
        )}
        <button type="button" className="btn btn-primary btn-block" onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}

// Encrypted-file cache (end-to-end transfers only: the bytes stay ciphertext,
// and the key never leaves the link). Entries expire with their transfer.
const CIPHER_CACHE = 'send-encrypted-files-v1';
const cipherKey = (fileId) => `/__send-cache/${fileId}`;

async function readCachedCipher(fileId) {
  try {
    if (!('caches' in window)) return null;
    const hit = await (await caches.open(CIPHER_CACHE)).match(cipherKey(fileId));
    if (!hit) return null;
    const expires = Date.parse(hit.headers.get('x-expires') || '');
    if (expires && expires < Date.now()) return null;
    return await hit.arrayBuffer();
  } catch {
    return null;
  }
}

function writeCachedCipher(fileId, buf, expiresAt) {
  try {
    if (!('caches' in window)) return;
    caches.open(CIPHER_CACHE)
      .then((c) => c.put(cipherKey(fileId), new Response(buf, { headers: { 'x-expires': expiresAt || '' } })))
      .catch(() => {});
  } catch { /* storage full or blocked — the next open just downloads again */ }
}

// Drop cached files whose transfer has expired.
(async () => {
  try {
    if (typeof window === 'undefined' || !('caches' in window)) return;
    const c = await caches.open(CIPHER_CACHE);
    for (const req of await c.keys()) {
      const r = await c.match(req);
      const expires = Date.parse(r?.headers.get('x-expires') || '');
      if (!expires || expires < Date.now()) await c.delete(req);
    }
  } catch { /* nothing to sweep */ }
})();

